import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import HelmetWrapper from "../../HelmetWrapper";
import { selectUser } from "../../components/auth/auth.selector";
import PageHeadingNew from "../../components/common-components/custom-comps/PageHeadingNew";
import CommonTable from "../../components/common-components/common-tables/CommonTable";
import GymFilter from "../../components/common-components/filters/gym-filter";
import { convertThenFormat } from "../../components/common-data/Converts";
import { fetchLeadAnalyticsGymwise } from "../../data/APIs";

/**
 * Lead Analytics — Gym-wise (ported read-only feature).
 * Source: GET /leads/analytics/gymwise (powered backend).
 * Shows per-gym total leads, converted count, revenue and conversion rate.
 */
const LeadAnalyticsGymwise = () => {
  const userInfoData = useSelector(selectUser);
  const token = userInfoData?.token;

  const [selectedGym, setSelectedGym] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(25);

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = {};
      if (selectedGym?.value) params.gym_id = selectedGym.value;
      const res = await fetchLeadAnalyticsGymwise(params, token);
      if (res?.status && Array.isArray(res.data)) {
        res.data.forEach((r, i) => {
          r._sno = i + 1;
        });
        setRows(res.data);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("Failed to load gym-wise lead analytics:", err);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedGym]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = useMemo(
    () => [
      {
        dataField: "_sno",
        text: "S.No",
        formatter: (_, row, idx) => <span className="tb-data">{row?._sno || idx + 1}</span>,
      },
      {
        dataField: "gym_name",
        text: "Gym Name",
        formatter: (_, row) => <span className="tb-data">{row?.gym_name || "NA"}</span>,
      },
      {
        dataField: "total_leads",
        text: "Total Leads",
        centerAligned: true,
        formatter: (_, row) => <span className="tb-data">{row?.total_leads || 0}</span>,
      },
      {
        dataField: "converted_count",
        text: "Converted",
        centerAligned: true,
        formatter: (_, row) => <span className="tb-data">{row?.converted_count || 0}</span>,
      },
      {
        dataField: "total_revenue",
        text: "Revenue",
        centerAligned: true,
        formatter: (_, row) => <span className="tb-data">₹{convertThenFormat(row?.total_revenue || 0)}</span>,
      },
      {
        dataField: "conversion_rate",
        text: "Conversion %",
        centerAligned: true,
        formatter: (_, row) => <span className="tb-data">{row?.conversion_rate || 0}%</span>,
      },
    ],
    []
  );

  const tablePagination = useMemo(() => {
    const total = rows.length;
    return total > 0 ? [{ pages: Math.ceil(total / pageLimit) || 1, pagination: total }] : null;
  }, [rows, pageLimit]);

  return (
    <HelmetWrapper title="WTF: Lead Analytics (Gym-wise)">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        <Row>
          <Col>
            <PageHeadingNew
              heading="Lead Analytics — Gym-wise"
              subheading="Per-gym leads, conversions and revenue"
              onReload={loadData}
            />
          </Col>
        </Row>

        <Row className="mt15">
          <Col md={4}>
            <GymFilter selectedGym={selectedGym} setSelectedGym={setSelectedGym} />
          </Col>
        </Row>

        <Row className="mt15">
          <Col>
            <div className="common-table-container tb-bordered">
              <CommonTable
                tableData={rows}
                tablePagination={tablePagination}
                tableColumns={columns}
                isPage={isPage}
                setIsPage={setIsPage}
                pageLimit={pageLimit}
                setPageLimit={(val) => {
                  setPageLimit(val);
                  setIsPage(0);
                }}
                isLoading={isLoading}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default LeadAnalyticsGymwise;
